<template>
  <div class="p-6">
    <div class="flex items-center justify-between mb-4">
      <div>
        <h2 class="text-sm font-semibold text-gray-900">Participant Roles</h2>
        <p class="text-xs text-gray-500 mt-0.5">Identify who's on each call -- provider, patient, translator, etc.</p>
      </div>
      <div class="flex items-center gap-3">
        <label class="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
          <input v-model="showInactive" type="checkbox" class="rounded border-gray-300 text-primary-600" />
          Show inactive
        </label>
        <button class="btn-primary-sm" @click="showAddForm = true">
          <FontAwesomeIcon :icon="['fas', 'plus']" class="mr-1" />Add role
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-8 text-gray-500">
      <FontAwesomeIcon :icon="['fas', 'spinner']" spin class="mr-1" />Loading...
    </div>

    <div v-else>
      <div v-if="showAddForm" class="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
        <input
          ref="addInput"
          v-model="newName"
          type="text"
          placeholder="Role name (e.g. Interpreter)"
          class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
          @keydown.enter="createRole"
          @keydown.escape="cancelAdd"
        />
        <div class="flex gap-2">
          <button class="btn-primary-sm text-xs" :disabled="!newName.trim() || creating" @click="createRole">
            {{ creating ? 'Creating...' : 'Create' }}
          </button>
          <button class="btn-secondary-sm text-xs" @click="cancelAdd">Cancel</button>
        </div>
        <p v-if="createError" class="text-xs text-red-600">{{ createError }}</p>
      </div>

      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
            <th class="py-2 px-3 font-semibold">Display Name</th>
            <th class="py-2 px-3 font-semibold">Name</th>
            <th class="py-2 px-3 font-semibold text-right">Status</th>
            <th class="py-2 px-3 font-semibold text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="role in roles"
            :key="role.id"
            class="border-b border-gray-100 last:border-0"
            :class="!role.is_active ? 'opacity-50' : ''"
          >
            <td class="py-2.5 px-3">
              <template v-if="editingId === role.id">
                <input
                  ref="editInput"
                  v-model="editDisplayName"
                  type="text"
                  placeholder="Display name"
                  class="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                  @keydown.enter="saveEdit(role)"
                  @keydown.escape="cancelEdit"
                />
              </template>
              <template v-else>
                <span class="font-medium text-gray-900">{{ role.display_name }}</span>
                <span v-if="role.is_system" v-tooltip="'This role is managed by Kairon. If you need help modifying it, reach out to support@kaironhealth.com.'" class="ml-1.5 inline-flex items-center gap-1 px-1.5 py-px text-[10px] font-medium rounded bg-gray-100 text-gray-500">
                  <FontAwesomeIcon :icon="['fas', 'lock']" /> System
                </span>
              </template>
            </td>
            <td class="py-2.5 px-3">
              <template v-if="editingId === role.id">
                <input
                  v-model="editInternalName"
                  type="text"
                  placeholder="Internal name"
                  class="border border-gray-300 rounded px-2 py-1 text-sm w-full font-mono"
                  @keydown.enter="saveEdit(role)"
                  @keydown.escape="cancelEdit"
                />
              </template>
              <span v-else class="font-mono text-xs text-gray-500">{{ role.name }}</span>
            </td>
            <td class="py-2.5 px-3 text-right">
              <span v-if="!role.is_active" class="text-xs text-gray-400">Inactive</span>
              <span v-else class="text-xs text-green-600">Active</span>
            </td>
            <td class="py-2.5 px-3 text-right">
              <template v-if="!role.is_system">
                <template v-if="editingId === role.id">
                  <button class="text-xs text-primary-600 hover:text-primary-800 mr-2" @click="saveEdit(role)">Save</button>
                  <button class="text-xs text-gray-500 hover:text-gray-700" @click="cancelEdit">Cancel</button>
                </template>
                <template v-else>
                  <button class="text-xs text-gray-500 hover:text-gray-700 mr-2" @click="startEdit(role)">Rename</button>
                  <button
                    class="text-xs"
                    :class="role.is_active ? 'text-red-500 hover:text-red-700' : 'text-primary-600 hover:text-primary-800'"
                    @click="toggleActive(role)"
                  >
                    {{ role.is_active ? 'Deactivate' : 'Activate' }}
                  </button>
                </template>
              </template>
            </td>
          </tr>
        </tbody>
      </table>

      <p v-if="!roles.length" class="text-center py-8 text-sm text-gray-500">
        No participant roles configured.
      </p>
    </div>
  </div>
</template>

<script>
import tooltip from '@/directives/tooltip'
import {
  listParticipantRoles,
  createParticipantRole,
  updateParticipantRole,
  toggleParticipantRoleActive,
} from '@/services/api/participantRoles'

export default {
  name: 'TelephonyParticipantRoles_Admin',
  directives: { tooltip },
  data() {
    return {
      roles: [],
      loading: true,
      showInactive: false,
      showAddForm: false,
      newName: '',
      creating: false,
      createError: null,
      editingId: null,
      editDisplayName: '',
      editInternalName: '',
    }
  },
  watch: {
    showInactive() {
      this.load()
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        this.roles = await listParticipantRoles({ includeInactive: this.showInactive })
      } catch {
        this.roles = []
      } finally {
        this.loading = false
      }
    },
    async createRole() {
      if (!this.newName.trim()) return
      this.creating = true
      this.createError = null
      try {
        await createParticipantRole({ display_name: this.newName.trim() })
        this.newName = ''
        this.showAddForm = false
        await this.load()
      } catch (e) {
        this.createError = e?.response?.data?.error || 'Could not create role.'
      } finally {
        this.creating = false
      }
    },
    cancelAdd() {
      this.showAddForm = false
      this.newName = ''
      this.createError = null
    },
    startEdit(role) {
      this.editingId = role.id
      this.editDisplayName = role.display_name
      this.editInternalName = role.name
      this.$nextTick(() => this.$refs.editInput?.[0]?.focus())
    },
    cancelEdit() {
      this.editingId = null
      this.editDisplayName = ''
      this.editInternalName = ''
    },
    async saveEdit(role) {
      const body = {}
      if (this.editDisplayName.trim() && this.editDisplayName.trim() !== role.display_name) {
        body.display_name = this.editDisplayName.trim()
      }
      if (this.editInternalName.trim() && this.editInternalName.trim() !== role.name) {
        body.name = this.editInternalName.trim()
      }
      if (!Object.keys(body).length) {
        this.cancelEdit()
        return
      }
      try {
        await updateParticipantRole(role.id, body)
        this.cancelEdit()
        await this.load()
      } catch {
        // keep editing
      }
    },
    async toggleActive(role) {
      try {
        await toggleParticipantRoleActive(role.id)
        await this.load()
      } catch {
        // silent
      }
    },
  },
}
</script>
